/**
 * useStudentLiveSessionsListState — les gestes de l'élève dans « Tous mes lives ».
 *
 * Depuis que ses prochains lives ont quitté « Messages » (Wajih, 18/10), la liste porte TOUS
 * les gestes de l'élève sur une séance — ceux de l'onglet, et l'absence qu'il prévient lui-même :
 *  - « GO ! » le jour du live → `pre-call`, si la séance le compte (`StudentLiveCardComponent.answerGo`),
 *    sinon « Tu n'es pas inscrit » ;
 *  - décaler, prévenir d'une absence, signaler un retard → les feuilles du parent
 *    (`useParentSessionGesture` : `<MoveSessionFlow>`, `<ReportLateFlow>`), dites à l'élève
 *    (`voice="student"`). Le drapeau et les créneaux partent par les appels de son ancien formulaire
 *    (`asStudent` : `reportAbsenceAsStudent`, `manage-attendance-flag`, gardée par la policy de la
 *    séance ; `getRecoveryPossibilities`) — ceux du parent écrivent dans `formData.user`, que l'espace
 *    de l'élève n'a pas, et le succès y tombait en échec (vu en recette). Le serveur écrit à la
 *    famille comme quand le parent le pose (`LiveSessionController::manageStudentAttendance`).
 *    Un cours individuel ou un essai mène, dans la feuille, au tuteur — sa conversation, `messenger`
 *    (la branche « Contacter mon tuteur » d'aujourd'hui) — ou à l'équipe ;
 *  - annuler une absence ou un retard → `useLiveSessionsListState` (confirmation, puis
 *    `cancelAttendanceFlag`) ;
 *  - une séance suspendue pour paiement touchée → la boîte du paiement, et rien d'autre (Wajih,
 *    19/10) : ni « GO ! », ni « Décaler », ni le retard, que le serveur refuse à un élève bloqué. Le
 *    menu d'aujourd'hui (`StudentLiveCardComponent.buildMenuItems`) ne lisait pas le paiement, et
 *    menait à ces refus ;
 *  - une séance clôturée touchée → « Cette session est clôturée » ;
 *  - « Planifier un rattrapage » → `recover-absence` (`withStudent: false`), comme l'onglet.
 *
 * Chaque bouton d'une boîte qui remplace une boîte legacy est mesuré sous le nom de l'ancienne
 * (`trackDialogButton`) : la série Amplitude continue à l'allumage.
 *
 * Ce qu'il possède (réducteur pur, exporté et testé) : les boîtes « Tu n'es pas inscrit »,
 * « Cette session est clôturée » et celle du paiement en attente.
 */
import { useCallback, useMemo, useReducer } from "react"
import { useNavigation } from "@react-navigation/native"

import { type LiveSessionAction } from "../../../formatters/liveSessionActions"
import { isPaymentBlocked } from "../../../formatters/liveSessionFormatter"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { DIALOG_CLOSE, trackDialogButton } from "../../../hooks/trackDialogButton"
import { studentMenuFeedback } from "./studentMenuFeedback"
import { studentRefusalText } from "../../../hooks/studentRefusal"
import { useParentSessionGesture } from "../../../hooks/useParentSessionGesture"
import { type UILiveSession, type UITutor } from "../../../types"
import { useLiveSessionsListState } from "../../parent/LiveSessions/useLiveSessionsListState"

type State = {
    /** Le « GO ! » d'une séance où l'élève n'est pas inscrit. */
    joinRefused: boolean
    /** Une séance suspendue touchée : la boîte du paiement. */
    paymentDialog: boolean
    /** Une séance clôturée touchée : « Cette session est clôturée ». */
    closedDialog: boolean
}

type Action =
    | { type: "REFUSE_JOIN" }
    | { type: "CLOSE_JOIN_REFUSAL" }
    | { type: "OPEN_PAYMENT" }
    | { type: "CLOSE_PAYMENT" }
    | { type: "OPEN_CLOSED" }
    | { type: "CLOSE_CLOSED" }

export const initialStudentLiveSessionsListState: State = {
    joinRefused: false,
    paymentDialog: false,
    closedDialog: false,
}

export function studentLiveSessionsListReducer(state: State, action: Action): State {
    switch (action.type) {
        case "REFUSE_JOIN":
            return { ...state, joinRefused: true }
        case "CLOSE_JOIN_REFUSAL":
            return { ...state, joinRefused: false }
        case "OPEN_PAYMENT":
            return { ...state, paymentDialog: true }
        case "CLOSE_PAYMENT":
            return { ...state, paymentDialog: false }
        case "OPEN_CLOSED":
            return { ...state, closedDialog: true }
        case "CLOSE_CLOSED":
            return { ...state, closedDialog: false }
        default:
            return state
    }
}

/** Les mots de `StudentLiveCardComponent.answerGo`, quand la séance ne compte pas l'élève. */
export const JOIN_REFUSAL_TEXTS = {
    title: "Tu n'es pas inscrit à cette session live",
    message: "Tu ne peux pas y accéder",
}

/** Les mots du « GO ! » d'une séance clôturée (`StudentLiveCardComponent.js:683-693`). */
export const CLOSED_SESSION_TEXTS = {
    title: "Cette session est clôturée",
    message: "Tu ne peux plus y accéder",
}

/** Les replis des refus, au tutoiement : un refus qui vouvoie et que l'app ne connaît pas les prend. */
export const GESTURE_FAILED = "Impossible d'enregistrer. Réessaie dans un instant."

const RECOVERY_REFUSED_HINT = "Tu peux tout de même prévenir de ton absence : la séance ne pourra pas être rattrapée."

/** Le titre de l'ancienne boîte « Gérer le rdv » d'une séance qui n'est pas en groupe. */
function manageRdvTitle(roomType: string | undefined): string {
    if (roomType === "trial") return "Séance d'essai"
    if (roomType === "support") return "Live de support technique"
    if (roomType === "custom") return "Live personnalisé"
    return "Séance individuelle"
}

/**
 * Une feuille du parent dite à l'élève : sa voix, et sans le prénom de l'enfant — l'élève lit
 * « ta séance », son prénom n'a rien à faire dans la phrase (`Addressee`).
 */
function toStudentVoice<P extends { childFirstname?: string }>(
    props: P
): Omit<P, "childFirstname" | "voice"> & { voice: "student" } {
    const student: Omit<P, "childFirstname" | "voice"> & { childFirstname?: string; voice?: unknown } = { ...props }
    delete student.childFirstname
    return { ...student, voice: "student" }
}

type StackNavigation = {
    push: (name: string, params?: Record<string, unknown>) => void
    navigate: (name: string, params?: Record<string, unknown>) => void
}

export type UseStudentLiveSessionsListStateArgs = {
    /** Les séances de la liste, triées : ce que les feuilles du geste proposent. */
    sessions: UILiveSession[]
    /** Les séances qui comptent l'élève : la garde de son « GO ! ». */
    enrolledSessionIds: string[]
    paymentUrl: string | null
    /** Relit les séances : après un drapeau posé, et à la fermeture de la boîte du paiement. */
    refresh: () => void
}

export function useStudentLiveSessionsListState(args: UseStudentLiveSessionsListStateArgs) {
    const { sessions, enrolledSessionIds, paymentUrl, refresh } = args
    const navigation = useNavigation<StackNavigation>()
    const [state, dispatchUi] = useReducer(studentLiveSessionsListReducer, initialStudentLiveSessionsListState)
    const menu = useLiveSessionsListState()

    // « Contacter mon tuteur » : SA conversation, comme la branche élève de « Gérer le rdv ».
    const openTutorConversation = useCallback(
        (tutor: UITutor) => navigation.navigate("messenger", { tutorId: tutor.id }),
        [navigation]
    )

    const gesture = useParentSessionGesture({
        sessions,
        surface: "live_sessions_list",
        onOpenTutorMessage: openTutorConversation,
        onCancelFlag: menu.actions.onLiveMenuAction,
        paymentUrl,
        refresh,
        asStudent: true,
    })
    const { onSessionAction: onGestureAction } = gesture

    const onSessionAction = useCallback(
        (session: UILiveSession, action: LiveSessionAction) => {
            // Défense en profondeur : la carte d'une séance suspendue n'offre que le paiement
            // (`onBlockedSessionPress`), mais un geste qui arriverait quand même n'ouvre que lui.
            if (isPaymentBlocked(session)) {
                dispatchUi({ type: "OPEN_PAYMENT" })
                return
            }
            if (action !== "join_as_child") {
                onGestureAction(session, action)
                return
            }
            if (!enrolledSessionIds.includes(String(session.id))) {
                dispatchUi({ type: "REFUSE_JOIN" })
                return
            }
            navigation.push("pre-call", { liveSessionId: session.liveSessionId })
        },
        [enrolledSessionIds, navigation, onGestureAction]
    )

    const closePayment = useCallback(() => {
        dispatchUi({ type: "CLOSE_PAYMENT" })
        // Le legacy relit les séances à la fermeture de sa boîte : le paiement a pu passer.
        refresh()
    }, [refresh])

    const { close, confirmCancel, closeCancel } = menu.actions
    const cancelTitle = menu.cancelConfirm?.title ?? ""

    const actions = useMemo(
        () => ({
            close,
            onSessionAction,
            onFinishedSessionPress: () => dispatchUi({ type: "OPEN_CLOSED" }),
            onBlockedSessionPress: () => dispatchUi({ type: "OPEN_PAYMENT" }),
            pay:
                paymentUrl != null
                    ? () => {
                          openExternalUrl(paymentUrl)
                          closePayment()
                      }
                    : undefined,
            closePayment,
            confirmJoinRefusal: () => {
                trackDialogButton(JOIN_REFUSAL_TEXTS.title, "OK")
                dispatchUi({ type: "CLOSE_JOIN_REFUSAL" })
            },
            dismissJoinRefusal: () => {
                trackDialogButton(JOIN_REFUSAL_TEXTS.title, DIALOG_CLOSE)
                dispatchUi({ type: "CLOSE_JOIN_REFUSAL" })
            },
            confirmClosed: () => {
                trackDialogButton(CLOSED_SESSION_TEXTS.title, "OK")
                dispatchUi({ type: "CLOSE_CLOSED" })
            },
            dismissClosed: () => {
                trackDialogButton(CLOSED_SESSION_TEXTS.title, DIALOG_CLOSE)
                dispatchUi({ type: "CLOSE_CLOSED" })
            },
            confirmCancel: () => {
                trackDialogButton(cancelTitle, "Oui, annuler")
                confirmCancel()
            },
            closeCancel: () => {
                trackDialogButton(cancelTitle, "Non")
                closeCancel()
            },
            planRecovery: () => navigation.push("recover-absence", { withStudent: false }),
        }),
        [close, onSessionAction, paymentUrl, closePayment, cancelTitle, confirmCancel, closeCancel, navigation]
    )

    // Les feuilles du parent, dites à l'élève : ses mots, tutoyés, ceux de son formulaire d'absence.
    // Les refus du serveur aussi : le parent garde ses phrases, l'élève lit les siennes
    // (`studentRefusalText`). L'étape « contacte ton tuteur / l'équipe » remplace l'ancienne boîte
    // « Gérer le rdv » : ses boutons sont mesurés sous les noms de celle-ci.
    const gestures = useMemo(() => {
        const { move, late, notice } = gesture.gestures
        const title = manageRdvTitle(move.selectedSession?.roomType)
        return {
            ...gesture.gestures,
            notice: { ...notice, message: notice.message && studentRefusalText(notice.message, GESTURE_FAILED) },
            move: {
                ...toStudentVoice(move),
                errorMessage: move.errorMessage && studentRefusalText(move.errorMessage, GESTURE_FAILED),
                slotsEmptyMessage:
                    move.slotsEmptyMessage && studentRefusalText(move.slotsEmptyMessage, RECOVERY_REFUSED_HINT),
                onMessageTutor: () => {
                    trackDialogButton(title, "Contacter mon tuteur")
                    move.onMessageTutor?.()
                },
                onContactWhatsApp: () => {
                    trackDialogButton(title, "WhatsApp")
                    move.onContactWhatsApp?.()
                },
                onContactEmail: () => {
                    trackDialogButton(title, "Email")
                    move.onContactEmail?.()
                },
            },
            late: {
                ...toStudentVoice(late),
                errorMessage: late.errorMessage && studentRefusalText(late.errorMessage, GESTURE_FAILED),
            },
        }
    }, [gesture.gestures])

    // Le retour du menu, dit à l'élève : un échec d'annulation prend son repli, un succès sa phrase.
    const studentMenu = useMemo(
        () => (menu.feedback == null ? menu : { ...menu, feedback: studentMenuFeedback(menu.feedback) }),
        [menu]
    )

    return { state, actions, menu: studentMenu, gestures }
}
